/**
 * Drawing on a model plane: the review canvas laid over the 3D view while a
 * plane is open. tldraw's camera is the plane's 2D pan and zoom, and it
 * drives the 3D camera square-on behind it (parallel, so one scale and
 * offset map plane to screen), which keeps every mark registered. With no
 * plane open, the planes' marks are drawn in the 3D view itself.
 */
import type {
  AutomergeUrl,
  DocHandle,
  DocHandleChangePayload,
} from "@automerge/automerge-repo";
import { RepoContext, useDocHandle, useDocument } from "@automerge/react";
import type { CameraView } from "bear/engine/threeConsumer";
import {
  annotationsOf,
  markOf,
  planesOf,
  type Plane,
} from "playground/annotations/annotations";
import type { TextDoc } from "playground/fs/types";
import {
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { react, type Editor, type TLShapeId } from "tldraw";
import { invariant } from "ts-invariant";

import { formatCameraView, parseCameraView } from "@/bear-viewer/camera-view";
import {
  planeCamera,
  planeCameraView,
  planeView,
  modelFrameOf,
  type ModelPlaneFrame,
} from "@/bear-viewer/model-plane";
import type { PlaneMark } from "@/bear-viewer/types";
import { Button } from "@/components/ui/button";
import {
  showView,
  subscribeViewTaken,
  isSandboxConnected,
  showPlaneMarks,
  subscribePlaneOpen,
  subscribeSandboxConnection,
} from "@/lib/bear-sandbox-bridge";
import { useSignedInUser } from "@/lib/session";
import { MarkThreadCard } from "@/review/mark-thread";
import { planeSvg } from "@/review/plane-svg";
import { ReviewCanvas } from "@/review/ReviewCanvas";

/**
 * A plane's marks as the 3D view shows them: everything but marks on resolved
 * threads (and bindings to those), which stay in the doc's history only.
 */
function visibleRecords(doc: TextDoc, plane: Plane) {
  const hidden = new Set(
    annotationsOf(doc).flatMap(({ resolved, anchor }) =>
      resolved && anchor.kind === "marks" && anchor.plane === plane.id
        ? anchor.shapes
        : [],
    ),
  );
  return Object.fromEntries(
    Object.entries(plane.records).filter(
      ([id, r]) => !hidden.has(id) && !hidden.has(String(r.toId ?? "")),
    ),
  );
}

export function useSandboxConnected() {
  const [connected, setConnected] = useState(isSandboxConnected);
  useEffect(() => subscribeSandboxConnection(setConnected), []);
  return connected;
}

/**
 * The viewed file's markup planes, drawn in the 3D view. Each plane's marks
 * are exported again only when they change; a pin click opens its plane.
 */
export function usePlaneMarks(
  docUrl: AutomergeUrl | undefined,
  open: string | null,
  onOpen: (planeId: string, frame: ModelPlaneFrame) => void,
) {
  const repo = useContext(RepoContext);
  const connected = useSandboxConnected();
  /** Marks are the doc's they were exported from; any other doc shows none. */
  const [shown, setShown] = useState<{
    docUrl: AutomergeUrl;
    doc: TextDoc;
    marks: PlaneMark[];
  } | null>(null);
  const current = shown?.docUrl === docUrl ? shown : null;

  useEffect(() => {
    if (!docUrl || !repo) return;
    let live = true;
    // Exports finish out of order; only the newest revision's may publish.
    let latest = 0;
    let exported = new Map<string, { key: string; mark: PlaneMark | null }>();
    const exportFrom = (doc: TextDoc) => {
      const revision = ++latest;
      // A model file's planes all sit in model space.
      const planes = planesOf(doc).map((plane) => ({
        plane,
        frame: modelFrameOf(plane),
        records: visibleRecords(doc, plane),
      }));
      void Promise.all(
        planes.map(async ({ plane, frame, records }) => {
          const key = JSON.stringify([frame, records]);
          const cached = exported.get(plane.id);
          if (cached?.key === key) return [plane.id, cached] as const;
          const image = await planeSvg({ ...plane, records });
          const mark = image && { id: plane.id, frame, ...image };
          return [plane.id, { key, mark }] as const;
        }),
      ).then(
        (entries) => {
          if (!live || revision !== latest) return;
          exported = new Map(entries);
          setShown({
            docUrl,
            doc,
            marks: entries.flatMap(([, { mark }]) => mark ?? []),
          });
        },
        (error: unknown) => {
          toast.error(
            `Couldn't draw the markup planes in 3D: ${error instanceof Error ? error.message : String(error)}`,
          );
        },
      );
    };
    const onChange = ({ doc }: DocHandleChangePayload<TextDoc>) => {
      invariant(doc, `No document at ${docUrl}`);
      exportFrom(doc);
    };
    let handle: DocHandle<TextDoc> | null = null;
    void repo.find<TextDoc>(docUrl).then((found) => {
      if (!live) return;
      handle = found;
      exportFrom(found.doc());
      found.on("change", onChange);
    });
    return () => {
      live = false;
      handle?.off("change", onChange);
    };
  }, [docUrl, repo]);

  const marks = current?.marks ?? NO_MARKS;
  const openPlane = open ? current?.doc[`plane:${open}`] : undefined;
  const openFrame = useMemo(
    () => (openPlane ? modelFrameOf(openPlane) : undefined),
    [openPlane],
  );
  useEffect(() => {
    if (!connected) return;
    showPlaneMarks({
      planes: marks,
      open: open && openFrame ? { id: open, section: openFrame.section } : null,
    });
  }, [connected, marks, open, openFrame]);

  const doc = current?.doc;
  useEffect(
    () =>
      subscribePlaneOpen((planeId) => {
        const plane = doc?.[`plane:${planeId}`];
        invariant(plane, `No plane ${planeId}`);
        onOpen(planeId, modelFrameOf(plane));
      }),
    [doc, onOpen],
  );
}

const NO_MARKS: PlaneMark[] = [];

/** A view that already shows the plane square-on, so drawing starts there. */
function isSquareOn(frame: ModelPlaneFrame, view: CameraView) {
  const [nx, ny, nz] = frame.normal;
  const [dx, dy, dz] = view.direction;
  return view.projection === "parallel" && nx * dx + ny * dy + nz * dz > 0.9999;
}

function viewportOf(editor: Editor) {
  const { w, h } = editor.getViewportScreenBounds();
  return { width: w, height: h };
}

/** The 3D view square-on behind the plane's camera. */
function viewBehind(editor: Editor, frame: ModelPlaneFrame): CameraView {
  return planeCameraView(frame, editor.getCamera(), viewportOf(editor));
}

/** Put the plane's camera on a URL's view: square-on to it, else its own. */
function showOnPlane(
  editor: Editor,
  frame: ModelPlaneFrame,
  cam: string | undefined,
) {
  const view = cam === undefined ? undefined : parseCameraView(cam);
  invariant(view !== null, `Not a camera view: ${cam}`);
  const start = view && isSquareOn(frame, view) ? view : planeView(frame);
  editor.setCamera(planeCamera(frame, start, viewportOf(editor)));
}

export interface PlaneMarkupProps {
  docUrl: AutomergeUrl;
  /** The model file the plane belongs to. */
  file: string;
  /** A thread on this plane to open: its mark is selected once drawn. */
  thread?: string;
  planeId: string;
  /** The URL's camera. */
  cam: string | undefined;
  /** Puts the plane's camera, at rest, in the URL. */
  onCamera: (cam: string) => void;
  /** Done: the plane closes and the camera stays where it put it. */
  onClose: () => void;
  /** Something else took the camera: the plane closes and gives up `cam`. */
  onViewTaken: () => void;
}

/**
 * An open plane owns the camera. The URL's `cam` (on opening, a followed
 * link, Back) moves the plane's camera; the plane's camera moves the 3D one
 * whenever a sandbox is there to show it; and at rest it is the URL's `cam`.
 * Mount one per plane: its state describes that plane's editor.
 */
export function PlaneMarkup({
  docUrl,
  file,
  thread,
  planeId,
  cam,
  onCamera,
  onClose,
  onViewTaken,
}: PlaneMarkupProps) {
  const handle = useDocHandle<TextDoc>(docUrl, { suspense: true });
  const [doc] = useDocument<TextDoc>(docUrl, { suspense: true });
  const plane = doc[`plane:${planeId}`];
  invariant(plane, `No plane ${planeId}`);
  // Keyed on the frame, not the plane: drawing changes the plane's records,
  // and Automerge keeps the unchanged frame's identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const frame = useMemo(() => modelFrameOf(plane), [plane.frame]);
  const user = useSignedInUser();
  const [editor, setEditor] = useState<Editor | null>(null);
  const connected = useSandboxConnected();

  /** The `cam` last shown on or written from the plane; null before any. */
  const shown = useRef<string | undefined | null>(null);
  useEffect(() => {
    if (!editor || cam === shown.current) return;
    shown.current = cam;
    showOnPlane(editor, frame, cam);
  }, [editor, frame, cam]);

  useEffect(() => {
    if (!editor || !connected) return;
    return react("plane camera drives the 3D view", () =>
      showView(viewBehind(editor, frame)),
    );
  }, [editor, frame, connected]);

  const writeCam = useEffectEvent(onCamera);
  useEffect(() => {
    if (!editor) return;
    return react("plane camera at rest is the URL's", () => {
      if (editor.getCameraState() !== "idle") return;
      const next = formatCameraView(viewBehind(editor, frame));
      if (next === shown.current) return;
      shown.current = next;
      writeCam(next);
    });
  }, [editor, frame]);

  // Following a reference selects its mark, which opens the thread's card.
  const target = thread ? doc[`comment:${thread}`] : undefined;
  const mark = target && markOf(doc, target);
  useEffect(() => {
    if (!editor || !mark) return;
    editor.select(mark.id as TLShapeId);
  }, [editor, mark?.id]);

  // The plane draws over the camera it drives; once something else moves the
  // camera (framing nodes), the marks would no longer sit on the model, so
  // the plane closes and the view stays where that put it.
  useEffect(() => subscribeViewTaken(onViewTaken), [onViewTaken]);

  const drive = useCallback((editor: Editor) => {
    editor.setCameraOptions({ wheelBehavior: "zoom" });
    setEditor(editor);
    return () => setEditor(null);
  }, []);

  return (
    <div className="plane-markup absolute inset-0 z-10">
      <style>{PLANE_MARKUP_CSS}</style>
      <ReviewCanvas
        handle={handle}
        planeId={planeId}
        author={{ id: user.id, name: user.name }}
        onMount={drive}
      />
      {editor ? (
        <MarkThreadCard
          editor={editor}
          handle={handle}
          doc={doc}
          planeId={planeId}
          file={file}
        />
      ) : null}
      <Button
        size="sm"
        variant="secondary"
        className="absolute top-2 left-2 z-[400] h-7 text-xs"
        onClick={onClose}
      >
        Done
      </Button>
    </div>
  );
}

/** The model shows through: no canvas background behind the marks. The style
 *  panel sits below the model's own run and options controls. */
const PLANE_MARKUP_CSS = `
.plane-markup .tl-container { --tl-color-background: transparent; background: transparent; }
.plane-markup .tl-background { display: none; }
.plane-markup .tlui-layout__top__right { margin-top: 40px; }
`;
